// The audio bridge protocol: the messages between the page and the voice's
// AudioWorklet processor, and the layout of the telemetry batches.
//
// The contract is docs/contracts/audio-bridge.md and the message format is
// schemas/bridge.schema.json. This file is shared by both sides: the page
// (engine.ts) and every processor (mock-processor.ts, later worklet.ts). The
// processor side is bundled into a worklet module by
// scripts/prebuild/05-build-worklet.mjs, so the code below must run in the
// AudioWorkletGlobalScope: no string decoding, no fine clock, no timers, and no
// allocation in what process() calls.

/** Version of the protocol. The processor sends it in hello and ready; the page refuses a mismatch. */
export const PROTOCOL_VERSION = 1;

/** The sample rate every live AudioContext runs at. */
export const SAMPLE_RATE = 48_000;

/** One engine cycle (two crank revolutions) in radians; θ in telemetry lies in [0, CYCLE). */
export const CYCLE = 4 * Math.PI;

// ---------------------------------------------------------------------------
// Messages

/** What the powertrain is built from: a module from the registry and the load on its shaft. */
export interface Powertrain {
  /** Registry id of the source module, for example "pulse-train". */
  source: string;
  load: {
    /** "free", "kinematic-dyno" now; "dyno", "vehicle", "propeller" with the dynamics tickets. */
    kind: string;
    /** Numeric parameters of the load, for example { rpm: 900 }. */
    params: Record<string, number>;
  };
}

/** A control change due at a frame of the voice's own clock (samples since init). */
export interface TimelineEvent {
  frame: number;
  control: string;
  value: number;
}

export type AssetKind = "ir" | "loopset" | "blob";

/** An asset the processor waits for before it reports ready. */
export interface AssetRef {
  id: string;
  kind: AssetKind;
}

/** Starts (or restarts) the voice. Sent again unchanged to recover after an error. */
export interface InitMessage {
  type: "init";
  protocol: number;
  /** The engine bundle as UTF-8 bytes; the processor never decodes text itself. */
  bundle: Uint8Array;
  powertrain: Powertrain;
  timeline?: TimelineEvent[];
  assets?: AssetRef[];
  /** The voice's WebAssembly bytes, for processors that run one (worklet.ts, #28). */
  wasm?: ArrayBuffer;
  /**
   * A render job file (TOML) as UTF-8 bytes. The wasm host then renders exactly this
   * job, sample for sample as the native renderer does, and takes neither the
   * powertrain's load nor `timeline` from init. The mock ignores it.
   */
  job?: Uint8Array;
}

export interface AssetMessage {
  type: "asset";
  id: string;
  kind: AssetKind;
  bytes: ArrayBuffer;
}

/** A discrete control change, by control id (host ids such as "quality", or module ids such as "rpm"). */
export interface ControlMessage {
  type: "control";
  id: string;
  value: number;
}

export interface StatsRequest {
  type: "stats";
}

/** Page to processor. A bare ArrayBuffer is a telemetry buffer coming home to the pool. */
export type ToProcessor = InitMessage | AssetMessage | ControlMessage | StatsRequest | ArrayBuffer;

/** Values the processor found in its global scope, sent with hello. */
export type ScopeProbe = Record<string, boolean | number | string | null>;

/** Posted once by the processor's constructor. */
export interface HelloMessage {
  type: "hello";
  protocol: number;
  /** Which processor this is: "mock" or "voice". */
  processor: string;
  sampleRate: number;
  /** Controls the processor takes as AudioParams (k-rate, named by control id). */
  params: string[];
  scope: ScopeProbe;
}

/** Posted when init and every asset it listed have arrived and the voice runs. */
export interface ReadyMessage {
  type: "ready";
  protocol: number;
  /** Milliseconds init took on the audio thread. */
  ms: number;
  /** currentFrame when the voice became ready. */
  frame: number;
  /** The longest block the voice accepts. */
  maxFrames: number;
  /** How many samples the audio lags the shaft state in telemetry (for the heard angle). */
  latencyFrames: number;
  /** Control ids the voice accepts, as port messages or AudioParams. */
  controls: string[];
  /** Names of the slow-state values, in the order they follow each telemetry record. */
  slow: string[];
  assets: { id: string; kind: AssetKind; bytes: number }[];
}

export type ErrorWhere = "init" | "asset" | "control" | "process" | "protocol" | "processor";

/**
 * Something went wrong. A fatal error leaves the voice silent until the page sends
 * init again; "processor" is raised by the page itself from the node's processorerror.
 */
export interface ErrorMessage {
  type: "error";
  where: ErrorWhere;
  message: string;
  /** currentFrame when it happened, -1 when unknown. */
  frame: number;
  fatal: boolean;
}

/** The telemetry pool's state, in answer to a stats request. */
export interface StatsMessage {
  type: "stats";
  /** Buffers the processor allocated, all in its constructor. */
  created: number;
  /** Buffers in the pool now. */
  free: number;
  /** Batches sent so far (the next sequence number). */
  sent: number;
  /** Buffers returned so far. */
  returned: number;
  /** Records dropped so far because the pool was empty. */
  dropped: number;
}

/** Processor to page. A bare ArrayBuffer is a telemetry batch (layout below). */
export type FromProcessor = HelloMessage | ReadyMessage | ErrorMessage | StatsMessage | ArrayBuffer;

// ---------------------------------------------------------------------------
// Telemetry layout
//
// A batch is a Float64Array over a pooled ArrayBuffer: HEADER values, then
// `count` records of `stride` values each. A record describes one render
// quantum: the shaft state at the block's first sample, and the timing of the
// process() call. Its slow-state values follow the fixed fields.

export const HEADER = 10;
/** Records in this batch. */
export const H_COUNT = 0;
/** Values per record: RECORD_FIELDS plus the number of slow-state values. */
export const H_STRIDE = 1;
/** Batch sequence number, from 0; a gap means a lost batch. */
export const H_SEQ = 2;
/** Records dropped so far because the pool was empty. */
export const H_DROPPED = 3;
export const H_SAMPLE_RATE = 4;
/** Milliseconds spent in process() so far (Date.now steps, summed). */
export const H_PROCESS_MS = 5;
/** process() calls so far. */
export const H_BLOCKS = 6;
/** Frames processed so far. */
export const H_FRAMES = 7;
/** Gaps between process() calls longer than the stall threshold, so far. */
export const H_STALLS = 8;
/** The longest gap between two process() calls since the previous batch, in ms. */
export const H_MAX_GAP_MS = 9;

export const RECORD_FIELDS = 10;
/** currentFrame at entry to process(). It can jump; it is not a block counter. */
export const R_FRAME = 0;
/** The voice's own clock: samples rendered since init, at the block's first sample. */
export const R_VOICE_FRAME = 1;
/** Whole engine cycles since init. */
export const R_CYCLE = 2;
/** Crank angle within the cycle, [0, CYCLE), at the block's first sample. */
export const R_THETA = 3;
/** Crank speed in rad/s. */
export const R_OMEGA = 4;
/** Date.now() at entry to process(). */
export const R_ENTRY_MS = 5;
/** Milliseconds spent in process() (1 ms steps). */
export const R_PROCESS_MS = 6;
/** Milliseconds since the previous process() call's entry. */
export const R_GAP_MS = 7;
/** Block length in frames, read on every call. */
export const R_LENGTH = 8;
/** FLAG_* bits. */
export const R_FLAGS = 9;

export const MAX_SLOW = 16;
export const RECORDS_PER_BATCH = 64;
/** Every pooled buffer has this size, whatever the slow-state count. */
export const BATCH_BYTES = 8 * (HEADER + RECORDS_PER_BATCH * (RECORD_FIELDS + MAX_SLOW));

/** The block is silence. */
export const FLAG_SILENT = 1;
/** The voice failed and waits for init. */
export const FLAG_FAILED = 2;
/** An exception was thrown in this block. */
export const FLAG_ERROR = 4;
/** No init yet (or assets still missing). */
export const FLAG_NOT_READY = 8;
/** The voice's clock is paused. */
export const FLAG_PAUSED = 16;

/** Defaults for the processor options. */
export const DEFAULT_POOL_SIZE = 16;
export const DEFAULT_BATCH_HZ = 60;
export const DEFAULT_STALL_MS = 30;

/** processorOptions every bridge processor reads. */
export interface BridgeProcessorOptions {
  /** Telemetry buffers to allocate; 16 ride out a main-thread stall of about 300 ms. */
  poolSize?: number;
  /** Most batches per second of audio time. */
  batchHz?: number;
  /** A gap between process() calls longer than this counts as a stall (a likely deadline miss). */
  stallMs?: number;
}

// ---------------------------------------------------------------------------
// Processor side

/**
 * Writes one telemetry record per block into pooled buffers and posts each full
 * batch with a transfer list. Every buffer is allocated in the constructor; when
 * the pool is empty, records are dropped and counted, never allocated for.
 */
export class TelemetryWriter {
  readonly created: number;
  private readonly port: MessagePort;
  private readonly free: (ArrayBuffer | null)[];
  private freeCount: number;
  private readonly interval: number;
  private readonly stallMs: number;
  private readonly sampleRate: number;
  private stride = RECORD_FIELDS;
  private batch: Float64Array | null = null;
  private count = 0;
  private lastBatchTime = 0;
  private lastEntry = -1;
  private maxGap = 0;
  sent = 0;
  returned = 0;
  dropped = 0;
  private processMs = 0;
  private blocks = 0;
  private frames = 0;
  private stalls = 0;

  constructor(port: MessagePort, sampleRate: number, options: BridgeProcessorOptions) {
    this.port = port;
    this.sampleRate = sampleRate;
    this.created = Math.max(1, Math.floor(options.poolSize ?? DEFAULT_POOL_SIZE));
    this.interval = 1 / (options.batchHz ?? DEFAULT_BATCH_HZ);
    this.stallMs = options.stallMs ?? DEFAULT_STALL_MS;
    this.free = new Array<ArrayBuffer | null>(this.created);
    for (let i = 0; i < this.created; i++) this.free[i] = new ArrayBuffer(BATCH_BYTES);
    this.freeCount = this.created;
  }

  get freeBuffers(): number {
    return this.freeCount;
  }

  /** Sets the number of slow-state values per record. Sends any open batch first. */
  setSlowCount(n: number): void {
    if (this.batch !== null) this.send(this.lastBatchTime);
    this.stride = RECORD_FIELDS + Math.min(Math.max(0, n), MAX_SLOW);
  }

  /** Takes a buffer back into the pool. Returns false for a buffer that is not one of ours. */
  give(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength !== BATCH_BYTES || this.freeCount >= this.created) return false;
    this.free[this.freeCount++] = buffer;
    this.returned++;
    return true;
  }

  /**
   * Records one block. `slow` holds at least the slow-state count values.
   * `audioTime` is the processor's currentTime, which paces the batches.
   */
  record(
    frame: number,
    voiceFrame: number,
    cycle: number,
    theta: number,
    omega: number,
    entryMs: number,
    processMs: number,
    length: number,
    flags: number,
    slow: Float64Array,
    audioTime: number,
  ): void {
    const gap = this.lastEntry < 0 ? 0 : entryMs - this.lastEntry;
    this.lastEntry = entryMs;
    if (gap > this.maxGap) this.maxGap = gap;
    if (gap > this.stallMs) this.stalls++;
    this.processMs += processMs;
    this.blocks++;
    this.frames += length;

    if (this.batch === null) {
      if (this.freeCount === 0) {
        this.dropped++;
        return;
      }
      const buffer = this.free[--this.freeCount]!;
      this.free[this.freeCount] = null;
      this.batch = new Float64Array(buffer); // one small view per batch, at most batchHz a second
      this.count = 0;
    }
    const b = this.batch;
    const i = HEADER + this.count * this.stride;
    b[i + R_FRAME] = frame;
    b[i + R_VOICE_FRAME] = voiceFrame;
    b[i + R_CYCLE] = cycle;
    b[i + R_THETA] = theta;
    b[i + R_OMEGA] = omega;
    b[i + R_ENTRY_MS] = entryMs;
    b[i + R_PROCESS_MS] = processMs;
    b[i + R_GAP_MS] = gap;
    b[i + R_LENGTH] = length;
    b[i + R_FLAGS] = flags;
    for (let s = 0; s < this.stride - RECORD_FIELDS; s++) b[i + RECORD_FIELDS + s] = slow[s] ?? 0;
    this.count++;
    if (this.count === RECORDS_PER_BATCH || audioTime - this.lastBatchTime >= this.interval) this.send(audioTime);
  }

  private send(audioTime: number): void {
    const b = this.batch;
    if (b === null) return;
    b[H_COUNT] = this.count;
    b[H_STRIDE] = this.stride;
    b[H_SEQ] = this.sent++;
    b[H_DROPPED] = this.dropped;
    b[H_SAMPLE_RATE] = this.sampleRate;
    b[H_PROCESS_MS] = this.processMs;
    b[H_BLOCKS] = this.blocks;
    b[H_FRAMES] = this.frames;
    b[H_STALLS] = this.stalls;
    b[H_MAX_GAP_MS] = this.maxGap;
    this.port.postMessage(b.buffer, [b.buffer]);
    this.batch = null;
    this.maxGap = 0;
    this.lastBatchTime = audioTime;
  }

  stats(): StatsMessage {
    return {
      type: "stats",
      created: this.created,
      free: this.freeCount,
      sent: this.sent,
      returned: this.returned,
      dropped: this.dropped,
    };
  }
}

// ---------------------------------------------------------------------------
// Page side

/** Read access to one telemetry batch. Valid only until its buffer goes back to the pool. */
export class TelemetryBatch {
  readonly data: Float64Array;

  constructor(buffer: ArrayBuffer) {
    this.data = new Float64Array(buffer);
  }

  get count(): number {
    return this.data[H_COUNT]!;
  }
  get stride(): number {
    return this.data[H_STRIDE]!;
  }
  get seq(): number {
    return this.data[H_SEQ]!;
  }
  get droppedTotal(): number {
    return this.data[H_DROPPED]!;
  }
  get sampleRate(): number {
    return this.data[H_SAMPLE_RATE]!;
  }
  get processMsTotal(): number {
    return this.data[H_PROCESS_MS]!;
  }
  get blocksTotal(): number {
    return this.data[H_BLOCKS]!;
  }
  get framesTotal(): number {
    return this.data[H_FRAMES]!;
  }
  get stallsTotal(): number {
    return this.data[H_STALLS]!;
  }
  get maxGapMs(): number {
    return this.data[H_MAX_GAP_MS]!;
  }
  get slowCount(): number {
    return this.stride - RECORD_FIELDS;
  }

  /** Field `field` (R_*) of record `i`. */
  get(i: number, field: number): number {
    return this.data[HEADER + i * this.stride + field]!;
  }

  /** Slow-state value `s` of record `i`. */
  slow(i: number, s: number): number {
    return this.data[HEADER + i * this.stride + RECORD_FIELDS + s]!;
  }
}

/** One telemetry record as a plain object, for tests and for keeping the newest state. */
export interface TelemetryRecord {
  frame: number;
  voiceFrame: number;
  cycle: number;
  theta: number;
  omega: number;
  entryMs: number;
  processMs: number;
  gapMs: number;
  length: number;
  flags: number;
  slow: number[];
}

export function readRecord(batch: TelemetryBatch, i: number): TelemetryRecord {
  const slow: number[] = [];
  for (let s = 0; s < batch.slowCount; s++) slow.push(batch.slow(i, s));
  return {
    frame: batch.get(i, R_FRAME),
    voiceFrame: batch.get(i, R_VOICE_FRAME),
    cycle: batch.get(i, R_CYCLE),
    theta: batch.get(i, R_THETA),
    omega: batch.get(i, R_OMEGA),
    entryMs: batch.get(i, R_ENTRY_MS),
    processMs: batch.get(i, R_PROCESS_MS),
    gapMs: batch.get(i, R_GAP_MS),
    length: batch.get(i, R_LENGTH),
    flags: batch.get(i, R_FLAGS),
    slow,
  };
}

/** A whole batch as a plain object: the decoded form that bridge.schema.json describes. */
export interface DecodedBatch {
  type: "telemetry";
  seq: number;
  stride: number;
  sampleRate: number;
  dropped: number;
  processMs: number;
  blocks: number;
  frames: number;
  stalls: number;
  maxGapMs: number;
  records: TelemetryRecord[];
}

export function decodeBatch(batch: TelemetryBatch): DecodedBatch {
  const records: TelemetryRecord[] = [];
  for (let i = 0; i < batch.count; i++) records.push(readRecord(batch, i));
  return {
    type: "telemetry",
    seq: batch.seq,
    stride: batch.stride,
    sampleRate: batch.sampleRate,
    dropped: batch.droppedTotal,
    processMs: batch.processMsTotal,
    blocks: batch.blocksTotal,
    frames: batch.framesTotal,
    stalls: batch.stallsTotal,
    maxGapMs: batch.maxGapMs,
    records,
  };
}

/** How a binary value appears when a message is described for the schema. */
export interface BinaryDescriptor {
  $binary: "ArrayBuffer" | "Uint8Array";
  byteLength: number;
}

/**
 * A message as JSON for the schema: every ArrayBuffer or Uint8Array becomes a
 * BinaryDescriptor, since JSON cannot hold the bytes that structured clone carries.
 */
export function describeMessage(message: unknown): unknown {
  if (message instanceof ArrayBuffer) return { $binary: "ArrayBuffer", byteLength: message.byteLength };
  if (message instanceof Uint8Array) return { $binary: "Uint8Array", byteLength: message.byteLength };
  if (Array.isArray(message)) return message.map(describeMessage);
  if (message !== null && typeof message === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(message)) out[k] = describeMessage(v);
    return out;
  }
  return message;
}
